import { fetchData } from "../APICalls/buildCalls";
import env from "../env.config";
import { CategoryType } from "../types";

/**
 * Ask whether the menu has moved past `knownMenuVersion`, and answer with the
 * fresh categories when it has.
 *
 * On-demand revalidation refreshes the HTML for the NEXT visitor. A tab that is
 * already open never re-requests it, and the item version create-receipt sends
 * is read from the menu lookups the menu fills -- so a guest who was mid-order
 * when the menu re-synced keeps sending the pre-sync version and is rejected
 * with ReqItemsContainsOutDatedItems, re-add after re-add. This is what updates
 * an open tab.
 *
 * Conditional: 304 is the common case and means the known version is current.
 *
 * Resolves to `undefined` when there is nothing to apply (304, an error status,
 * a body that is not a list) and to the categories when there is -- INCLUDING
 * an empty list. A 200 with `[]` is the answer when every category was deleted,
 * and it has to reach the tab like any other menu: treating it as "no update"
 * left a tab that was open through the deletion showing the menu it had loaded
 * (qahwa, eg, 2026-10-04).
 *
 * Nothing is written to local storage: the page is regenerated on demand, so
 * the caller's Redux state is the only copy a live tab needs, and a stored one
 * would outlive the menu it describes.
 *
 * An empty answer carries no `restaurant.menuVersion` to learn from, so the
 * caller's known version does not advance and the next event asks again. That
 * costs one 200 per socket event, not a loop.
 */
export const fetchMenuIfVersionChanged = async (
  restaurantId: number,
  knownMenuVersion: number
): Promise<CategoryType[] | undefined> => {
  const res = await fetchData(
    `/category/details/${restaurantId}?menuVersion=${knownMenuVersion}`,
    env.apiV2
  );

  if (res.status !== 200) {
    res.body?.cancel();

    return undefined;
  }

  const categories: CategoryType[] = await res.json();

  if (!Array.isArray(categories)) {
    return undefined;
  }

  return categories;
};

/** The response carries `restaurant` alongside the declared CategoryType fields. */
export const readMenuVersion = (
  categories: CategoryType[]
): number | undefined =>
  (categories[0] as CategoryType & { restaurant?: { menuVersion?: number } })
    ?.restaurant?.menuVersion;

/** The later of two menu versions; menu versions only ever move forward. */
export const newerMenuVersion = (
  a: number | undefined,
  b: number | undefined
): number | undefined => {
  if (a === undefined) {
    return b;
  }

  return b === undefined ? a : Math.max(a, b);
};

/**
 * Whether a menu at `menuVersion` makes the cart's lines stale.
 *
 * A cart with no stamp (persisted before the stamp existed) or a menu answer
 * with no version (every category deleted) is treated as stale: that is what
 * the resync did unconditionally before, and dropping a cart costs a re-add,
 * while keeping a stale one costs a rejected checkout.
 */
export const isCartBehindMenu = (
  cartMenuVersion: number | undefined,
  menuVersion: number | undefined
): boolean =>
  cartMenuVersion === undefined ||
  menuVersion === undefined ||
  menuVersion > cartMenuVersion;
